import { Trans, useTranslation } from 'next-i18next'

import {
  Heading3,
  LinkItem,
  LinkList,
  LinkText,
  Paragraph,
  Section3
} from '@/features/Listings/seo/elements'
import AverageRentFaq from '@/features/Listings/seo/search-results-content/content/average-rent-faq'
import getFilterLinks from '@/features/Listings/seo/search-results-content/content/get-filter-links'
import ContentTemplate from '@/features/Listings/seo/search-results-content/content/hood/content-template'
import useFilterSlug from '@/features/Listings/seo/search-results-content/content/use-filter-slug'

import Amenities from '../amenities'

const GrandviewWoodland = ({
  filterSlug,
  cityName,
  cityNameWithState,
  cityData,
  neighborhoodName,
  neighbourhoodSlug,
  isChinese
}) => {
  const { t } = useTranslation()
  const { state, slug: citySlug } = cityData
  const { hasFilter, filterName } = useFilterSlug(filterSlug)

  const links = getFilterLinks(false, citySlug, neighbourhoodSlug)

  const neighborhoodNameWithCity = `${neighborhoodName}, ${cityName}`
  const neighbourhoodWithCityAndState = `${neighborhoodName}, ${cityNameWithState}`

  const getAbout = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.grandview-woodland-vancouver:About Grandview-Woodland Paragraph">
          Grandview-Woodland in{' '}
          <LinkText href="/rental-listings/city/vancouver">Vancouver</LinkText>,{' '}
          <LinkText href="/rental-listings/british-columbia">BC</LinkText>,
          offers a distinctive living experience, marked by its cultural
          diversity and vibrant community. Known for its eclectic and close-knit
          atmosphere, the neighborhood is particularly celebrated for{' '}
          <LinkText href="https://www.destinationvancouver.com/vancouver/neighbourhoods/commercial-drive/">
            Commercial Drive
          </LinkText>
          , a lively hub with a variety of multicultural restaurants, unique
          shops, and community events. The area is highly convenient, with most
          amenities within walking distance and good public transport links,
          including bus routes and nearby{' '}
          <LinkText href="https://www.translink.ca/schedules-and-maps/skytrain">
            SkyTrain
          </LinkText>{' '}
          stations. While Grandview-Woodland is not directly on the seawall and
          a bit farther from Vancouver’s major hiking trails and beaches, it
          does offer access to local parks like{' '}
          <LinkText href="https://vancouversbestplaces.com/city-of-vancouver/john-hendry-park/trout-lake/">
            Trout Lake
          </LinkText>{' '}
          in{' '}
          <LinkText href="https://covapp.vancouver.ca/parkfinder/parkdetail.aspx?inparkid=85">
            John Hendry Park
          </LinkText>
          , making it a suitable choice for those who appreciate urban living
          with a community feel and occasional outdoor activities.
        </Trans>
      </Paragraph>
    )
  }

  const getRentingIn = () => {
    const filterNameWithFallback = hasFilter
      ? filterName
      : t('seo:an apartment or room to rent')

    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo:Renting FILTER in Neighbourhood Paragraph">
            Finding {{ filterNameWithFallback }} in{' '}
            {{ neighborhoodNameWithCity }} requires research, communication with{' '}
            <LinkText href="/landlords">landlords & property managers</LinkText>
            , securing references, and signing a lease. All this can add up to a
            lengthy endeavour if you’re not familiar with the rental process in
            Canada. To help you get started, here is a list of resources with
            tips on renting in Canada.
          </Trans>
        </Paragraph>
        {isChinese ? (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/zh/%e7%a7%9f%e8%b5%81%e6%b3%95/%e5%8a%a0%e6%8b%bf%e5%a4%a74%e7%9c%81%e7%a7%9f%e6%88%bf%e6%8a%bc%e9%87%91%e3%80%81%e7%bb%ad%e7%ba%a6%e3%80%81%e7%a7%9f%e9%87%91%e6%b6%a8%e5%b9%85%e3%80%81%e6%94%af%e4%bb%98%e6%9d%a1%e6%ac%be%e6%af%94/">
              如何在加拿大租房？
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/liv%E7%A7%9F%E6%88%BF/%E5%9C%A8%E5%8A%A0%E6%8B%BF%E5%A4%A71000-%E6%9C%88%E8%83%BD%E7%A7%9F%E5%88%B0%E4%BB%80%E4%B9%88%E6%A0%B7%E7%9A%84%E6%88%BF/">
              租$1000可以在加拿大租到什么房？
            </LinkItem>
          </LinkList>
        ) : (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/renters/how-does-rent-work-in-canada-a-provincial-comparison/">
              How does rent work in Canada?
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/neighbourhoods/2020-renting-in-canada-what-1800-gets-you-across-canada/">
              What $1800 in rent can get you across Canada
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/renters/rent-in-canada-how-to-secure-a-home-before-coming-to-canada/">
              How to secure a home before arriving in Canada
            </LinkItem>
          </LinkList>
        )}
      </>
    )
  }

  const getAverageRent = () => {
    return (
      <AverageRentFaq
        filterSlug={filterSlug}
        cityName={neighborhoodNameWithCity}
        cityNameWithState={neighbourhoodWithCityAndState}
        citySlug={citySlug}
        neighbourhoodSlug={neighbourhoodSlug}
        rentAmounts={{
          bed1: '$2,300 - $2,900',
          bed2: '$3,000 - $4,000',
          bed3: '$3,500 - $4,800'
        }}
        rentReportCity={t('cities:city_name_vancouver')}
        rentReportLink="https://liv.rent/blog/rent-reports/vancouver/"
        rentReportLinkZh="https://liv.rent/blog/zh/%E7%A7%9F%E9%87%91%E7%BB%9F%E8%AE%A1/%E6%B8%A9%E5%93%A5%E5%8D%8E/"
      />
    )
  }

  const getApartmentHunting = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.hood.grandview-woodland-vancouver:Apartment Hunting Grandview-Woodland Paragraph 1">
            Finding an apartment for rent in Grandview-Woodland, Vancouver, BC
            requires careful planning and consideration. Start by determining
            your criteria and having all necessary documents ready for your
            application. Once you find an ideal apartment, review the{' '}
            <LinkText href="https://liv.rent/blog/rental-laws/residential-tenancy-agreement-explained/">
              lease agreement
            </LinkText>{' '}
            thoroughly, paying attention to terms, payment schedule, and
            restrictions. Consider{' '}
            <LinkText href="https://liv.rent/blog/lifestyle/finding-a-roommate-during-covid19/">
              roommates
            </LinkText>{' '}
            or co-living options to split costs. Take advantage of building
            amenities but be aware that they may increase monthly rent.{' '}
            <LinkText href="https://liv.rent/blog/renters/checklist-prepare-to-move-house/">
              Plan your move in advance
            </LinkText>
            , booking necessary services and notifying utility companies.
            Finally, embrace the vibrant Strathcona lifestyle by exploring your
            new neighborhood and connecting with your neighbors.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo.hood.grandview-woodland-vancouver:Apartment Hunting Grandview-Woodland Paragraph 2">
            In addition to choosing your location, it’s important to know what
            rental types are available (e.g.{' '}
            <LinkText href={links.bed1}>1 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed2}>2 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed3}>3 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.studio}>studio apartments</LinkText>) and the
            average rent for each property type. Refer below to figure out your
            options - ranging from{' '}
            <LinkText href={links.furnished}>furnished rentals</LinkText> and{' '}
            <LinkText href={links.unfurnished}>unfurnished rentals</LinkText> to{' '}
            <LinkText href={links.longTerm}>long term rentals</LinkText> and{' '}
            <LinkText href={links.shortTerm}>short term rentals</LinkText>. To
            start your search, you can refer to our guides on{' '}
            <LinkText href="https://liv.rent/blog/neighbourhoods/british-columbia-neighbourhoods/">
              neighbourhoods in BC
            </LinkText>
            .
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentalTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Apartments for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Apartments for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.grandview-woodland-vancouver:Apartments for rent in Grandview-Woodland Paragraph">
              <LinkText href={links.apartment}>
                Apartments for rent in Grandview-Woodland
              </LinkText>
              , Vancouver, BC provide a diverse array of options for those
              seeking a comfortable and convenient urban lifestyle. These
              apartments come in various sizes and styles, catering to a wide
              range of residents including individuals, couples, and families.
              Many feature contemporary amenities such as in-suite laundry,
              modern kitchens, and spacious living areas, offering a blend of
              comfort and style
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Houses for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Houses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.grandview-woodland-vancouver:Houses for rent in Grandview-Woodland Paragraph">
              <LinkText href={links.house}>
                Houses for rent in Grandview-Woodland
              </LinkText>{' '}
              offer the unique charm of this eclectic neighborhood. These homes
              vary in architectural styles, from historic character houses to
              sleek, modern designs. Renting a house here often means enjoying
              private outdoor space, ideal for gardening, outdoor activities, or
              pet owners. With options ranging from cozy single-family homes to
              larger properties, these houses are perfect for those seeking more
              space and a personal touch in their living environment.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Townhouses for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Townhouses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.grandview-woodland-vancouver:Townhouses for rent in Grandview-Woodland Paragraph">
              Grandview-Woodland also features{' '}
              <LinkText href={links.townhouse}>townhouses for rent</LinkText>,
              providing a unique mix of privacy and community. These multi-level
              units often include multiple bedrooms, ensuite bathrooms, and
              private outdoor spaces like balconies or patios. Townhouses in
              Grandview-Woodland offer independent living with the benefits of
              being part of a community, some even featuring their own garages.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Basements for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Basements for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.grandview-woodland-vancouver:Basements for rent in Grandview-Woodland Paragraph">
              Basements for rent in Grandview-Woodland are a popular choice for
              those seeking affordable housing options. These lower-level units,
              ranging from compact studios to more spacious one or two-bedroom
              layouts, often have separate entrances, offering privacy and
              affordability in a sought-after neighborhood.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Condos for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Condos for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.grandview-woodland-vancouver:Condos for rent Grandview-Woodland Paragraph">
              <LinkText href={links.apartment}>
                Condos for rent in Grandview-Woodland
              </LinkText>{' '}
              offer a more modern, luxurious living experience. Found in both
              high-rise and low-rise buildings, these condos provide amenities
              like fitness centers, communal outdoor spaces, and sometimes even
              pools, catering to those seeking a sophisticated and convenient
              urban lifestyle.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Rooms for rent LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Rooms for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.grandview-woodland-vancouver:Rooms for rent in Grandview-Woodland Paragraph">
              <LinkText href={links.room}>
                Rooms for rent in Grandview-Woodland
              </LinkText>{' '}
              are ideal for individuals preferring a shared living setup. This
              option, which can range from a room in a larger house to a shared
              apartment, is an affordable way to enjoy living in this dynamic
              neighborhood, providing opportunities for social interaction and
              community building.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getSpecialTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Pet friendly rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Pet friendly rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Pet friendly rentals NEIGHBOURHOOD Paragraph">
              Though not every landlord is keen on allowing pets, there are a
              good number of{' '}
              <LinkText href={links.petFriendly}>
                pet-friendly rentals in {{ neighborhoodNameWithCity }}
              </LinkText>{' '}
              for those with furry friends. Both{' '}
              <LinkText href={links.dogFriendly}>dog-friendly</LinkText> and{' '}
              <LinkText href={links.catFriendly}>cat-friendly</LinkText> rental
              units are available, as well as properties where both are allowed.
              Try searching for pet-friendly units using search filters on your
              rental platform to find units that match your needs.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Furnished rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Furnished rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Furnished rentals in NEIGHBOURHOOD Paragraph">
              If you’re new to town or just haven’t had a chance to pick up
              furniture of your own yet, not to worry – there are plenty of{' '}
              <LinkText href={links.furnished}>furnished rentals</LinkText>{' '}
              available in {{ neighborhoodNameWithCity }}. Renters seeking
              furnished units should search for these units using filters, but
              do be prepared to pay more in rent. There are{' '}
              <LinkText
                href={
                  isChinese
                    ? 'https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e7%a7%9f%e5%ae%a2%e9%a1%bb%e7%9f%a5%ef%bc%9a%e5%87%ba%e7%a7%9f%e5%8d%95%e4%bd%8d%e5%b8%a6%e5%ae%b6%e5%85%b7%e8%bf%98%e6%98%af%e4%b8%8d%e5%b8%a6%e5%ae%b6%e5%85%b7-%e7%a7%9f%e9%87%91%e5%8c%ba%e5%88%ab/'
                    : 'https://liv.rent/blog/renters/renting-furnished-unfurnished/'
                }
              >
                pros and cons to renting furnished units compared to unfurnished
                units
              </LinkText>
              , so deciding whether it’s worth it to buy furniture when moving
              is a good first step.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Short term rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Short term rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.grandview-woodland-vancouver:Short term rentals in Grandview-Woodland Paragraph">
              Grandview-Woodland, a vibrant and diverse neighborhood in
              Vancouver, is celebrated for its eclectic culture and
              community-oriented atmosphere. Located close to the city’s
              commercial and entertainment hubs, it offers a unique mix of
              residential charm and urban accessibility. While it may not be a
              central hub for major employment opportunities or academic
              institutions, Grandview-Woodland is popular for its array of local
              businesses, arts scene, and the famous Commercial Drive, known for
              its rich array of shops and eateries.{' '}
              <LinkText href={links.shortTerm}>Short-term rentals</LinkText> in
              this area are in demand, particularly for those seeking to
              experience its lively community spirit and convenience to downtown
              Vancouver. The area’s blend of cultural diversity, bustling
              commercial areas, and the availability of various housing options
              make it an{' '}
              <LinkText href="https://liv.rent/blog/landlords/short-term-vs-long-term-rentals-which-are-right-for-you/">
                attractive choice for short-term stays
              </LinkText>
              , appealing to visitors and locals who wish to immerse themselves
              in one of Vancouver’s most dynamic neighborhoods.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Cheap rentals in LOCATION"
              values={{
                location: neighborhoodNameWithCity
              }}
              defaults="Cheap rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Cheap rentals in NEIGHBOURHOOD Generic Paragraph">
              If you’re looking for{' '}
              <LinkText href={links.under1000}>
                cheap rentals in {{ neighborhoodNameWithCity }}
              </LinkText>
              , try finding a roommate or two and explore shared housing; or
              search for <LinkText href={links.room}>rooms for rent</LinkText>.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getExploring = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.grandview-woodland-vancouver:Exploring Grandview-Woodland Paragraph">
          Grandview-Woodland, located in Vancouver, British Columbia, is a
          vibrant neighborhood that is worth exploring for its unique blend of
          history, culture, and natural beauty. Situated on the eastern side of
          Downtown Vancouver, this area is known for its diverse community and
          charming residential architecture. A highlight of Grandview-Woodland
          is the{' '}
          <LinkText href="https://covapp.vancouver.ca/parkfinder/parkdetail.aspx?inparkid=52">
            Grandview Park
          </LinkText>
          , a local green space that offers a peaceful retreat from the city’s
          hustle and bustle. The neighborhood also boasts a thriving arts scene,
          with numerous galleries and studios showcasing the talents of local
          artists. Whether you are a history buff or simply looking to immerse
          yourself in the local culture, Grandview-Woodland is a must-visit
          destination in Vancouver.
        </Trans>
      </Paragraph>
    )
  }

  const getThingsToDo = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.grandview-woodland-vancouver:Things To Do Grandview-Woodland Paragraph">
          Grandview-Woodland, a lively neighborhood in Vancouver, BC, is packed
          with an array of exciting activities and attractions for both locals
          and tourists to enjoy. Nature enthusiasts can bask in the breathtaking
          beauty of Grandview Park, which offers picturesque trails that are
          perfect for hiking and biking adventures. Art aficionados will find
          their creative spark at the numerous vibrant galleries and studios
          scattered throughout the neighborhood, showcasing a diverse range of
          contemporary and historical artworks. Food enthusiasts can embark on a
          culinary journey by exploring the local eateries and food markets,
          where they can relish delectable dishes made with fresh, locally
          sourced ingredients. Those in search of a unique shopping experience
          can peruse the trendy boutiques and artisanal shops that line the
          streets of Grandview-Woodland. Moreover, the neighborhood’s rich
          history and cultural heritage are brought to life through various
          landmarks and heritage sites, providing visitors with a glimpse into
          the past. With its harmonious blend of natural beauty, artistic
          expression, gastronomic delights, and cultural experiences,
          Grandview-Woodland is an absolute must-visit destination that promises
          something for everyone.
        </Trans>
      </Paragraph>
    )
  }

  const getRestaurants = () => {
    return (
      <Trans i18nKey="seo.hood.grandview-woodland-vancouver:Restaurants Grandview-Woodland Paragraph">
        <Paragraph>
          Grandview-Woodland, located in Vancouver, BC, is a haven for food
          enthusiasts seeking delightful dining experiences. This vibrant
          neighborhood boasts a dynamic food scene that caters to all taste
          buds. Whether you’re craving international cuisine or local
          delicacies, Grandview-Woodland has a wide range of top-notch
          restaurants that deserve a spot on your must-visit list.
        </Paragraph>
        <Paragraph>
          1){' '}
          <LinkText href="https://viateverepizzeria.com/">
            Via Tevere Pizzeria Napoletana
          </LinkText>{' '}
          - Renowned for its authentic Neapolitan pizza, this restaurant is a
          favorite among locals and visitors alike, known for its wood-fired
          pizzas and cozy ambiance.
        </Paragraph>
        <Paragraph>
          2){' '}
          <LinkText href="https://www.jamjarcanteen.ca/">
            Jamjar Canteen
          </LinkText>{' '}
          - Offering a contemporary take on Lebanese cuisine, Jamjar Canteen is
          celebrated for its flavorful dishes, made with fresh, locally-sourced
          ingredients.
        </Paragraph>
        <Paragraph>
          3){' '}
          <LinkText href="https://theburrowrestaurant.com/">
            The Burrow
          </LinkText>{' '}
          - A popular spot for vegetarian and vegan Mexican cuisine, Bandidas
          Taqueria is known for its creative tacos, burritos, and a great
          selection of local craft beers.
        </Paragraph>
        <Paragraph>
          4){' '}
          <LinkText href="https://soprasotto.ca/">
            Sopra Sotto Pizzeria
          </LinkText>{' '}
          - Another gem for Italian cuisine lovers, Sopra Sotto serves a variety
          of Italian dishes with a focus on fresh, high-quality ingredients and
          traditional techniques.
        </Paragraph>
        <Paragraph>
          5){' '}
          <LinkText href="https://www.havanavancouver.com/">
            Havana Vancouver
          </LinkText>{' '}
          - Inspired by the flavors of Cuba, Latin America, and the Caribbean,
          Havana Vancouver is not only about great food but also a vibrant
          atmosphere, often featuring live performances and art exhibitions.
        </Paragraph>
      </Trans>
    )
  }

  const getTransportation = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.grandview-woodland-vancouver:Transportation Grandview-Woodland Paragraph">
          Transportation options in Grandview-Woodland, Vancouver, BC are
          abundant and easily accessible. The neighborhood benefits from an
          extensive{' '}
          <LinkText href="https://www.translink.ca/">
            public transit system
          </LinkText>
          , comprising buses,{' '}
          <LinkText href="https://www.translink.ca/schedules-and-maps/skytrain">
            SkyTrain
          </LinkText>
          , and
          <LinkText href="https://www.translink.ca/schedules-and-maps/seabus">
            SeaBus
          </LinkText>
          . These options offer residents and tourists the convenience to
          explore not only Grandview-Woodland but also other parts of the city.
          For those who enjoy cycling, the area boasts an extensive network of
          bike lanes and rental services, making it a cyclist’s paradise.
          Additionally, Grandview-Woodland is renowned for its
          pedestrian-friendly streets, with numerous attractions and amenities
          within walking distance. If driving is preferred, there are ample
          parking options available, including street parking and parking
          garages. In summary, Grandview-Woodland offers a diverse range of
          transportation choices to cater to the needs of all travelers.
        </Trans>
      </Paragraph>
    )
  }

  const getWhereToStay = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.grandview-woodland-vancouver:Where To Stay Grandview-Woodland Paragraph">
          Accommodation options in Grandview-Woodland, Vancouver, BC, are
          well-suited for those seeking an authentic local experience, as the
          area is not characterized by conventional hotel offerings. This
          vibrant and culturally diverse neighborhood offers a range of{' '}
          <LinkText href={links.shortTerm}>
            short-term rental properties
          </LinkText>{' '}
          available through platforms like{' '}
          <LinkText href="https://www.airbnb.ca/">Airbnb</LinkText>. These
          options vary from cozy rooms in character homes to entire apartments,
          providing an immersive experience in a community known for its
          eclectic charm and rich cultural tapestry. While Grandview-Woodland
          does not host a significant number of bed-and-breakfasts or boutique
          guesthouses, its residential nature and array of rental properties
          make it an appealing choice for travelers looking for a more personal
          touch.
        </Trans>
      </Paragraph>
    )
  }

  const content = {
    about: getAbout(),
    rentingIn: getRentingIn(),
    averageRent: getAverageRent(),
    apartmentHunting: getApartmentHunting(),
    rentalTypes: getRentalTypes(),
    specialTypes: getSpecialTypes(),
    amenities: (
      <Amenities
        cityName={neighborhoodNameWithCity}
        filterSlug={filterSlug}
        links={links}
      />
    ),
    exploring: getExploring(),
    thingsToDo: getThingsToDo(),
    restaurants: getRestaurants(),
    transportation: getTransportation(),
    whereToStay: getWhereToStay()
  }

  return (
    <ContentTemplate
      content={content}
      filterSlug={filterSlug}
      cityName={cityName}
      cityNameWithState={cityNameWithState}
      citySlug={citySlug}
      state={state}
      neighborhoodName={neighborhoodName}
      neighbourhoodSlug={neighbourhoodSlug}
    />
  )
}

export default GrandviewWoodland
